import { Box, Paper, keyframes, useMediaQuery, useTheme } from "@mui/material";
import { purple } from '@mui/material/colors';
import debounce from "lodash/debounce";
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import ColorService, { PUZZLE_GRID_COLORS } from "../../../services/ColorService";
import { GRID_HINT_WORD_LIMIT } from "../common/crosswordHintWordLimit";
import {
    WORD_REVEAL_ILLUMINATE_MS,
    WORD_REVEAL_LETTER_STAGGER_MS,
    wordRevealLetterPopKeyframes,
} from "../common/components/WordRevealFx";
import {
    findMatchingWord,
    findWordInGrid,
    isWordAlreadyFound,
    normalizeWord,
} from "../common/utils/PuzzlePlayLogic";
import { gridCoordGetFirstPosition } from "../common/utils/playHintTargets";

/** Entrance pop when a paid letter-hint cell is revealed. */
const paidHintCellPop = keyframes`
  0% {
    transform: scale(0.72);
    box-shadow: 0 0 0 0 rgba(249, 168, 37, 0);
    filter: brightness(1.35);
  }
  45% {
    transform: scale(1.18);
    box-shadow: 0 0 0 5px rgba(249, 168, 37, 0.55), 0 0 16px rgba(255, 213, 79, 0.9);
    filter: brightness(1.15);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 2px rgba(249, 168, 37, 0.35), 0 0 8px rgba(255, 193, 7, 0.45);
    filter: brightness(1);
  }
`;

/** Soft ongoing pulse so hinted letters stay easy to spot. */
const paidHintCellPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 2px rgba(249, 168, 37, 0.4), 0 0 6px rgba(255, 193, 7, 0.35);
    filter: brightness(1);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(249, 168, 37, 0.55), 0 0 14px rgba(255, 213, 79, 0.75);
    filter: brightness(1.08);
  }
`;

const paidHintLetterBounce = keyframes`
  0% { transform: scale(0.6); opacity: 0.35; }
  55% { transform: scale(1.22); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const GridGrid = forwardRef(({
    grid = [],
    words = [],
    wordsFound = [],
    onWordFound,
    onClickedWords,
    gridSize = 12,
    /** When false, aspect ratio follows columns×rows (browse Mini cards). Default square for play. */
    forceSquareAspect = true,
    isFullscreen = false,
    isBoldFont = false,
    filledCoordinates = [],
    isHintMode = false,
    highlightCells = [],
    hiddenMessageCells = [],
    /** Paid letter-hint cells (first N letters) with distinct background. */
    paidHintRevealCells = [],
    /** ADMIN: fixed cell font size in px; when set, overrides responsive clamp. */
    cellFontPx,
    /** When set with mobile viewport, grid letters use exactly this many px (e.g. hidden message play). Ignored when {@link cellFontPx} is set. */
    mobileFixedLetterPx,
    /** When set with mobile viewport, grid letters use at least this many px (e.g. word search play). Ignored when {@link cellFontPx} is set. */
    mobileMinLetterPx,
    /** If set, first-letter hints use this order (same as words-to-find list). If omitted, order follows filledCoordinates (legacy). */
    hintOrderedWords = null,
    /** Remaining hint slots from server `hintUsed` (cap at {@link GRID_HINT_WORD_LIMIT}). */
    hintRevealWordLimit,
    /** Map `"row,col"` → stagger index for word-reveal letter pop */
    celebrateCellIndexByKey = null,
}, ref) => {

    const effectiveHintWordCap =
        typeof hintRevealWordLimit === "number" &&
        Number.isFinite(hintRevealWordLimit) &&
        hintRevealWordLimit >= 0
            ? Math.min(GRID_HINT_WORD_LIMIT, hintRevealWordLimit)
            : GRID_HINT_WORD_LIMIT;

    const theme = useTheme();
    /** Match GridPlayMain and layout (`breakpoints.down('md')`) so font rules apply to the same viewports. */
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [selectedCells, setSelectedCells] = useState([]);
    const [isSelecting, setIsSelecting] = useState(false);
    const [startCell, setStartCell] = useState(null);
    const [clickedCells, setClickedCells] = useState([]);
    const [foundWordCells, setFoundWordCells] = useState([]);
    const selectedCellsRef = useRef([]);
    const isSelectingRef = useRef(false);
    const startCellRef = useRef(null);
    const dragMovedRef = useRef(false);

    selectedCellsRef.current = selectedCells;
    isSelectingRef.current = isSelecting;

    /** Build word from cells in path order (letter-wheel style). */
    const getWordFromCellsInOrder = (cells) => {
        if (!cells?.length) return "";
        return cells
            .map((cell) => {
                if (
                    cell.row >= 0 &&
                    cell.row < grid.length &&
                    cell.col >= 0 &&
                    cell.col < (grid[cell.row]?.length ?? 0)
                ) {
                    return grid[cell.row][cell.col];
                }
                return "";
            })
            .join("");
    };

    // Click-to-spell path historically sorted; keep that for multi-click selection.
    const getWordFromCells = (cells) => {
        if (cells.length === 0) return "";

        const sortedCells = [...cells].sort((a, b) => {
            if (a.row !== b.row) return a.row - b.row;
            return a.col - b.col;
        });

        return getWordFromCellsInOrder(sortedCells);
    };

    /**
     * Straight line in one of 8 directions from start toward end.
     * Off-axis pointers snap to the nearest direction (no infinite loop).
     */
    const getCellsBetween = (start, end) => {
        if (!start) return [];
        if (start.row === end.row && start.col === end.col) {
            return [{ row: start.row, col: start.col }];
        }

        const dr = end.row - start.row;
        const dc = end.col - start.col;

        // 8 directions: E, SE, S, SW, W, NW, N, NE (row+, col+)
        const dirs = [
            { dy: 0, dx: 1 },
            { dy: 1, dx: 1 },
            { dy: 1, dx: 0 },
            { dy: 1, dx: -1 },
            { dy: 0, dx: -1 },
            { dy: -1, dx: -1 },
            { dy: -1, dx: 0 },
            { dy: -1, dx: 1 },
        ];
        const angle = Math.atan2(dr, dc);
        const octant = Math.round(angle / (Math.PI / 4));
        const { dy, dx } = dirs[((octant % 8) + 8) % 8];

        let steps;
        if (dy === 0) steps = Math.abs(dc);
        else if (dx === 0) steps = Math.abs(dr);
        else steps = Math.max(Math.abs(dr), Math.abs(dc));

        const maxSteps = Math.max(gridSize, grid.length, 1) * 2;
        steps = Math.min(Math.max(0, steps), maxSteps);

        const cells = [];
        for (let i = 0; i <= steps; i += 1) {
            const row = start.row + dy * i;
            const col = start.col + dx * i;
            if (row < 0 || col < 0 || row >= grid.length || col >= (grid[row]?.length ?? 0)) {
                break;
            }
            cells.push({ row, col });
        }
        return cells.length ? cells : [{ row: start.row, col: start.col }];
    };

    const handleCellClick = (row, col) => {
        // Ignore synthetic click after a drag-select.
        if (dragMovedRef.current) {
            dragMovedRef.current = false;
            return;
        }
        setClickedCells((prev) => {
            const isAlreadyClicked = prev.some((cell) => cell.row === row && cell.col === col);
            let newCells;

            if (isAlreadyClicked) {
                newCells = prev.filter((cell) => !(cell.row === row && cell.col === col));
            } else {
                newCells = [...prev, { row, col }];
            }

            const currentWord = getWordFromCells(newCells);
            debouncedOnClickedWords(currentWord, newCells);

            return newCells;
        });
    };

    const handleCellMouseDown = (row, col, event) => {
        event?.preventDefault?.();
        dragMovedRef.current = false;
        // New drag clears any existing click/selection highlight.
        debouncedOnClickedWords.cancel();
        setClickedCells([]);
        if (onClickedWords) onClickedWords("", []);
        const start = { row, col };
        isSelectingRef.current = true;
        startCellRef.current = start;
        setIsSelecting(true);
        setStartCell(start);
        setSelectedCells([start]);
    };

    const handleCellMouseEnter = (row, col) => {
        if (!isSelectingRef.current || !startCellRef.current) return;
        dragMovedRef.current = true;
        const cells = getCellsBetween(startCellRef.current, { row, col });
        setSelectedCells(cells);
    };

    const finishDragSelection = () => {
        if (!isSelectingRef.current) return;
        isSelectingRef.current = false;
        startCellRef.current = null;

        const cells = selectedCellsRef.current;
        const selectedWord = getWordFromCellsInOrder(cells);
        const matchingWord =
            cells.length >= 2 ? findMatchingWord(selectedWord, words) : null;

        if (
            matchingWord &&
            !isWordAlreadyFound(normalizeWord(matchingWord), wordsFound)
        ) {
            onWordFound?.(matchingWord);
        }

        setIsSelecting(false);
        setSelectedCells([]);
        setStartCell(null);
    };

    const handleCellMouseUp = () => {
        finishDragSelection();
    };

    // Complete drag even if pointer is released outside the grid.
    useEffect(() => {
        if (!isSelecting) return undefined;
        const onWindowMouseUp = () => finishDragSelection();
        window.addEventListener("mouseup", onWindowMouseUp);
        return () => window.removeEventListener("mouseup", onWindowMouseUp);
        // finishDragSelection closes over latest words/wordsFound via refs + state reads at call time
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only toggle listener with isSelecting
    }, [isSelecting, words, wordsFound, onWordFound]);

    const dragPreviewWord = useMemo(() => {
        if (!isSelecting || selectedCells.length < 2) return "";
        return getWordFromCellsInOrder(selectedCells);
    }, [isSelecting, selectedCells, grid]);

    const dragMatchedWord = useMemo(() => {
        if (!dragPreviewWord) return null;
        const match = findMatchingWord(dragPreviewWord, words);
        if (!match) return null;
        if (isWordAlreadyFound(normalizeWord(match), wordsFound)) return null;
        return match;
    }, [dragPreviewWord, words, wordsFound]);

    const isCellSelected = (row, col) => {
        return selectedCells.some((cell) => cell.row === row && cell.col === col);
    };

    // Debounce parent callback so UI (cell background) updates immediately; logic runs 300ms after last click
    const debouncedOnClickedWords = useMemo(
        () => debounce((word, cells) => {
            if (onClickedWords) onClickedWords(word, cells);
        }, 300),
        [onClickedWords]
    );
    useEffect(() => () => debouncedOnClickedWords.cancel(), [debouncedOnClickedWords]);

    /** Keep green found cells in sync when parent restores or updates `wordsFound`. */
    useEffect(() => {
        if (!filledCoordinates?.length || !wordsFound?.length) {
            if (!wordsFound?.length) {
                setFoundWordCells([]);
            }
            return;
        }
        const seen = new Set();
        const merged = [];
        wordsFound.forEach((word) => {
            const cells = findWordInGrid(word, filledCoordinates);
            if (!cells?.length) return;
            cells.forEach((cell) => {
                const key = `${cell.row},${cell.col}`;
                if (seen.has(key)) return;
                seen.add(key);
                merged.push(cell);
            });
        });
        setFoundWordCells(merged);
    }, [wordsFound, filledCoordinates]);

    const isCellClicked = (row, col) => {
        const isClicked = clickedCells.some(cell => cell.row === row && cell.col === col);
        return isClicked;
    };

    const isCellFound = (row, col) => {
        const isFound = foundWordCells.some(cell => cell.row === row && cell.col === col);
        return isFound;
    };

    const isCellHighlighted = (row, col) => {
        return highlightCells.some(cell => cell.row === row && cell.col === col);
    };

    const isCellHiddenMessage = (row, col) => {
        return hiddenMessageCells.some(cell => cell.row === row && cell.col === col);
    };

    const isPaidHintRevealCell = (row, col) => {
        return paidHintRevealCells.some(
            (cell) => Number(cell.row) === Number(row) && Number(cell.col) === Number(col),
        );
    };

    const paidHintRevealIndex = useMemo(() => {
        const map = new Map();
        (paidHintRevealCells || []).forEach((cell, index) => {
            if (cell == null) return;
            map.set(`${Number(cell.row)},${Number(cell.col)}`, index);
        });
        return map;
    }, [paidHintRevealCells]);

    // Get the highlight color for a cell based on its character index
    const getHighlightColor = (row, col) => {
        const cell = highlightCells.find(cell => cell.row === row && cell.col === col);
        if (!cell || cell.charIndex === undefined) {
            return PUZZLE_GRID_COLORS.ORANGE; // Default orange if no charIndex
        }

        // Different shades of orange based on character position (0, 1, 2, ...)
        const colors = ColorService.getHighlightColors();

        const charIndex = cell.charIndex || 0;
        return colors[Math.min(charIndex, colors.length - 1)];
    };

    // Function to clear clicked cells (for clear button)
    const clearClickedCells = () => {
        debouncedOnClickedWords.cancel();
        setClickedCells([]);
        setFoundWordCells([]);
        if (onClickedWords) onClickedWords('', []);
    };

    // Function to clear only current selection (clicked cells) without clearing found words
    const clearSelection = () => {
        debouncedOnClickedWords.cancel();
        setClickedCells([]);
        setSelectedCells([]);
        setIsSelecting(false);
        setStartCell(null);
        startCellRef.current = null;
        isSelectingRef.current = false;
        if (onClickedWords) onClickedWords('', []);
    };


    // Function to move clicked cells to found (when word is found)
    const markClickedCellsAsFound = (specificCells = null) => {
        debouncedOnClickedWords.cancel();
        const cellsToMove = specificCells ? [...specificCells] : [...clickedCells];
        setClickedCells([]);
        setTimeout(() => {
            setFoundWordCells(prev => [...prev, ...cellsToMove]);
        }, 0);
        if (onClickedWords) onClickedWords('', []);
    };

    // Expose functions to parent component
    useImperativeHandle(ref, () => ({
        clearFoundWords: clearClickedCells, // For clear all found words
        clearSelection: clearSelection, // For clear only current selection
        markClickedCellsAsFound: markClickedCellsAsFound // For when word is found
    }));

    const isFirstCharacter = (row, col, filledCoordinates, wordsFoundList = []) => {
        if (!filledCoordinates) return false;
        if (effectiveHintWordCap <= 0) return false;
        const coords = Array.isArray(filledCoordinates) ? filledCoordinates : (typeof filledCoordinates === "string" ? (() => { try { return JSON.parse(filledCoordinates); } catch { return []; } })() : []);
        const r = Number(row);
        const c = Number(col);
        const foundSet = new Set((wordsFoundList || []).map(w => (w || "").trim().toLowerCase()));

        let limited;
        if (Array.isArray(hintOrderedWords) && hintOrderedWords.length > 0) {
            const byWord = new Map();
            for (const coord of coords) {
                const w = (coord.text || coord.word || "").trim();
                if (!w) continue;
                const key = w.toLowerCase();
                if (!byWord.has(key)) byWord.set(key, coord);
            }
            limited = [];
            for (const w of hintOrderedWords) {
                const key = w.trim().toLowerCase();
                if (foundSet.has(key)) continue;
                const coord = byWord.get(key);
                if (coord == null) continue;
                if (gridCoordGetFirstPosition(coord) == null) continue;
                limited.push(coord);
                if (limited.length >= effectiveHintWordCap) break;
            }
        } else {
            const eligible = coords.filter((coord) => {
                const wordInCoord = (coord.text || coord.word || "").trim().toLowerCase();
                if (wordInCoord && foundSet.has(wordInCoord)) return false;
                return gridCoordGetFirstPosition(coord) != null;
            });
            limited = eligible.slice(0, effectiveHintWordCap);
        }
        return limited.some((coord) => {
            const firstPos = gridCoordGetFirstPosition(coord);
            if (firstPos == null) return false;
            return Number(firstPos.row) === r && Number(firstPos.col) === c;
        });
    };

    return (
        <Box sx={{ position: "relative", width: "100%", height: "100%", minWidth: 0, minHeight: 0 }}>
            {isSelecting && dragPreviewWord ? (
                <Box
                    aria-live="polite"
                    sx={{
                        position: "absolute",
                        left: "50%",
                        top: 8,
                        transform: "translateX(-50%)",
                        zIndex: 4,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        fontWeight: 800,
                        letterSpacing: 1.5,
                        fontSize: "0.95rem",
                        pointerEvents: "none",
                        bgcolor: dragMatchedWord
                            ? "rgba(46, 125, 50, 0.92)"
                            : "rgba(25, 55, 40, 0.78)",
                        color: "#fff",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
                        maxWidth: "90%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {dragMatchedWord || dragPreviewWord}
                </Box>
            ) : null}
        <Paper
            elevation={3}
            sx={{
                p: 1,
                backgroundColor: PUZZLE_GRID_COLORS.BACKGROUND_LIGHT,
                userSelect: 'none',
                display: 'grid',
                /* minmax(0, 1fr): borders/gaps cannot force tracks wider than the card (avoids right-edge overflow). */
                gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))`,
                gridAutoRows: 'minmax(0, 1fr)',
                gap: 0.25,
                borderRadius: 1,
                border: `2px solid ${PUZZLE_GRID_COLORS.GRID_OUTER_BORDER}`,
                boxSizing: 'border-box',
                /* Match crossword `CrossGrid`: fill the play column width (no 520px cap). */
                width: '100%',
                maxWidth: '100%',
                height: '100%',
                maxHeight: '100%',
                aspectRatio: forceSquareAspect
                    ? '1'
                    : `${gridSize} / ${Math.max(Array.isArray(grid) ? grid.length : gridSize, 1)}`,
                minWidth: 0,
                minHeight: 0,
                transition: 'all 0.3s ease',
                margin: '0 auto',
                flexShrink: 1,
                overflow: 'hidden'
            }}
        >
            {grid.map((row, rowIndex) =>
                row.map((cell, colIndex) => (
                    <Box
                        key={`${rowIndex}-${colIndex}`}
                        data-puzzle-cell={`${rowIndex},${colIndex}`}
                        sx={{
                            width: '100%',
                            height: '100%',
                            minHeight: 0,
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: (() => {
                                const isSelected = isCellSelected(rowIndex, colIndex);
                                const isFound = isCellFound(rowIndex, colIndex);
                                const isClicked = isCellClicked(rowIndex, colIndex);
                                const isHighlighted = isCellHighlighted(rowIndex, colIndex);
                                const isFirstChar = isFirstCharacter(rowIndex, colIndex, filledCoordinates, wordsFound);
                                const isHiddenMessage = isCellHiddenMessage(rowIndex, colIndex);
                                const isPaidHint = isPaidHintRevealCell(rowIndex, colIndex);

                                let color = PUZZLE_GRID_COLORS.WHITE; // default

                                // Hidden message path: special color until those cells are found, then green
                                if (isHiddenMessage) {
                                    color = isFound
                                        ? PUZZLE_GRID_COLORS.GREEN_FOUND
                                        : PUZZLE_GRID_COLORS.HIDDEN_MESSAGE_BG;
                                } else if (isSelected) {
                                    color = dragMatchedWord
                                        ? PUZZLE_GRID_COLORS.GREEN_FOUND
                                        : PUZZLE_GRID_COLORS.BLUE_SELECTED;
                                } else if (isHighlighted) {
                                    color = getHighlightColor(rowIndex, colIndex);  // Different color for each character position
                                } else if (isPaidHint && !isFound) {
                                    color = "#FFE082"; // Amber — paid first-letter hint
                                } else if (isClicked) {
                                    color = PUZZLE_GRID_COLORS.YELLOW_CLICKED;
                                } else if (isHintMode && isFirstChar) {
                                    // Show first-letter hint even on found cells so hint is visible
                                    color = purple[100]; // Light purple from MUI palette
                                } else if (isFound) {
                                    color = PUZZLE_GRID_COLORS.GREEN_FOUND;
                                }

                                return color;
                            })(),
                            zIndex: isPaidHintRevealCell(rowIndex, colIndex) && !isCellFound(rowIndex, colIndex) ? 2 : 1,
                            ...(isPaidHintRevealCell(rowIndex, colIndex) && !isCellFound(rowIndex, colIndex)
                                ? {
                                      animation: `${paidHintCellPop} 0.55s cubic-bezier(0.22, 1, 0.36, 1) both, ${paidHintCellPulse} 1.5s ease-in-out infinite`,
                                      animationDelay: `${
                                          (paidHintRevealIndex.get(`${rowIndex},${colIndex}`) % 2) * 90
                                      }ms, ${
                                          550 +
                                          ((paidHintRevealIndex.get(`${rowIndex},${colIndex}`) % 2) * 90)
                                      }ms`,
                                  }
                                : {}),
                            color: (() => {
                                const isHiddenMessage = isCellHiddenMessage(rowIndex, colIndex);
                                const isSelected = isCellSelected(rowIndex, colIndex);
                                
                                if (isHiddenMessage) {
                                    const isFound = isCellFound(rowIndex, colIndex);
                                    return isFound
                                        ? PUZZLE_GRID_COLORS.TEXT_DARK
                                        : PUZZLE_GRID_COLORS.HIDDEN_MESSAGE_TEXT;
                                }

                                if (isPaidHintRevealCell(rowIndex, colIndex) && !isCellFound(rowIndex, colIndex)) {
                                    return "#5D4037";
                                }

                                return isSelected ? PUZZLE_GRID_COLORS.WHITE : PUZZLE_GRID_COLORS.TEXT_DARK;
                            })(),
                            fontFamily: "'Open Sans', sans-serif",
                            fontSize: (() => {
                                if (Number.isFinite(cellFontPx)) {
                                    return `${cellFontPx}px`;
                                }
                                if (isMobile) {
                                    if (
                                        typeof mobileFixedLetterPx === "number" &&
                                        Number.isFinite(mobileFixedLetterPx) &&
                                        mobileFixedLetterPx > 0
                                    ) {
                                        return `${mobileFixedLetterPx}px`;
                                    }
                                    if (
                                        typeof mobileMinLetterPx === "number" &&
                                        Number.isFinite(mobileMinLetterPx) &&
                                        mobileMinLetterPx > 0
                                    ) {
                                        return `clamp(${mobileMinLetterPx}px, 2.2vw, 0.9rem)`;
                                    }
                                    return "clamp(0.55rem, 2.2vw, 0.9rem)";
                                } else if (isFullscreen) {
                                    return "clamp(0.9rem, 2.2vw, 1.75rem)";
                                } else {
                                    return "clamp(0.7rem, 1.35vw, 1.05rem)";
                                }
                            })(),
                            fontWeight: (() => {
                                const isHiddenMessage = isCellHiddenMessage(rowIndex, colIndex);
                                // Hidden message cells always bold for emphasis
                                if (isHiddenMessage) {
                                    return 700;
                                }
                                if (isPaidHintRevealCell(rowIndex, colIndex)) {
                                    return 700;
                                }
                                return isBoldFont ? 700 : 400;
                            })(),
                            cursor: 'pointer',
                            border: `1px solid ${PUZZLE_GRID_COLORS.BORDER_LIGHT}`,
                            borderRadius: 0.25,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: (() => {
                                    const isHiddenMessage = isCellHiddenMessage(rowIndex, colIndex);
                                    const isSelected = isCellSelected(rowIndex, colIndex);
                                    const isHighlighted = isCellHighlighted(rowIndex, colIndex);
                                    const isClicked = isCellClicked(rowIndex, colIndex);
                                    const isFound = isCellFound(rowIndex, colIndex);
                                    const isPaidHint = isPaidHintRevealCell(rowIndex, colIndex);
                                    
                                    if (isHiddenMessage) {
                                        return isFound
                                            ? PUZZLE_GRID_COLORS.GREEN_FOUND_HOVER
                                            : PUZZLE_GRID_COLORS.HIDDEN_MESSAGE_HOVER;
                                    }

                                    if (isSelected) {
                                        return PUZZLE_GRID_COLORS.BLUE_SELECTED_HOVER;
                                    } else if (isHighlighted) {
                                        const baseColor = getHighlightColor(rowIndex, colIndex);
                                        // Darken the color on hover
                                        const hoverColors = ColorService.getHighlightHoverColors();
                                        return hoverColors[baseColor] || PUZZLE_GRID_COLORS.ORANGE_HOVER_1;
                                    } else if (isPaidHint && !isFound) {
                                        return "#FFD54F";
                                    } else if (isClicked) {
                                        return PUZZLE_GRID_COLORS.YELLOW_CLICKED_HOVER;
                                    } else if (isFound) {
                                        return PUZZLE_GRID_COLORS.GREEN_FOUND_HOVER;
                                    }
                                    
                                    return PUZZLE_GRID_COLORS.HOVER_BACKGROUND;
                                })()
                            }
                        }}
                        onMouseDown={(event) => handleCellMouseDown(rowIndex, colIndex, event)}
                        onMouseEnter={() => handleCellMouseEnter(rowIndex, colIndex)}
                        onMouseUp={handleCellMouseUp}
                        onClick={() => handleCellClick(rowIndex, colIndex)}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: "inline-block",
                                lineHeight: 1,
                                willChange:
                                    celebrateCellIndexByKey?.[`${rowIndex},${colIndex}`] != null ||
                                    (isPaidHintRevealCell(rowIndex, colIndex) &&
                                        !isCellFound(rowIndex, colIndex))
                                        ? "transform"
                                        : undefined,
                                ...(celebrateCellIndexByKey?.[`${rowIndex},${colIndex}`] != null
                                    ? {
                                          animation: `${wordRevealLetterPopKeyframes} ${WORD_REVEAL_ILLUMINATE_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both`,
                                          animationDelay: `${
                                              celebrateCellIndexByKey[`${rowIndex},${colIndex}`] *
                                              WORD_REVEAL_LETTER_STAGGER_MS
                                          }ms`,
                                      }
                                    : isPaidHintRevealCell(rowIndex, colIndex) &&
                                        !isCellFound(rowIndex, colIndex)
                                      ? {
                                            animation: `${paidHintLetterBounce} 0.55s cubic-bezier(0.22, 1, 0.36, 1) both`,
                                            animationDelay: `${
                                                ((paidHintRevealIndex.get(`${rowIndex},${colIndex}`) ??
                                                    0) %
                                                    2) *
                                                90
                                            }ms`,
                                            fontWeight: 800,
                                        }
                                      : {}),
                            }}
                        >
                            {cell}
                        </Box>
                    </Box>
                ))
            )}
        </Paper>
        </Box>
    );
});

GridGrid.displayName = 'GridGrid';

export default GridGrid;
